import React, { useEffect, useId, useState } from 'react';
import Dialog from '../../common/Dialog';
import { StatusPill, useToast } from '../../common';
import { ReferralsService } from '../../../services/referrals.service';
import type { PossibleDuplicatePatient, ReferralIntakeRequest } from '../../../dtos/clinical/referral.dto';
import { fetchPatientById, mapPatientResponse } from '../../../services/admin/patient.service';
import { toIsoDate } from '../../../services/patientmr/patientLifecycle.service';
import { REFERRAL_SOURCE_OPTIONS, MISSING_INFO_LABELS } from '../../../constants/patientLifecycle';
import type { PatientData } from '../../../dtos/admin/patient.dto';
import { disciplineReferralLabel } from '../../../constants/disciplines';
import { useServicesOffered } from '../../../hooks/useServicesOffered';

export interface IntakeDialogProps {
  open: boolean;
  onCancel: () => void;
  /** Open (select + navigate to) a patient chart: the new pending patient or an existing duplicate */
  onOpenPatient: (patient: PatientData) => void;
}

interface IntakeForm {
  firstName: string;
  lastName: string;
  dob: string;
  gender: string;
  phoneNumber: string;
  source: string;
  discipline: string;
  receivedDate: string;
  referralDate: string;
  requestedSOCDate: string;
  referringProvider: string;
  referringNPI: string;
  referringPhone: string;
  referringFax: string;
  notes: string;
}

const emptyForm = (): IntakeForm => ({
  firstName: '',
  lastName: '',
  dob: '',
  gender: '',
  phoneNumber: '',
  source: '',
  discipline: '',
  receivedDate: toIsoDate(new Date()),
  referralDate: '',
  requestedSOCDate: '',
  referringProvider: '',
  referringNPI: '',
  referringPhone: '',
  referringFax: '',
  notes: '',
});

type RequiredKey = 'firstName' | 'lastName' | 'dob' | 'source' | 'discipline' | 'receivedDate';
const REQUIRED: RequiredKey[] = ['firstName', 'lastName', 'dob', 'source', 'discipline', 'receivedDate'];

const opt = (value: string): string | undefined => (value.trim() ? value.trim() : undefined);

export function buildIntakeRequest(form: IntakeForm, confirmNotDuplicate: boolean): ReferralIntakeRequest {
  return {
    patient: {
      firstName: form.firstName.trim(),
      lastName: form.lastName.trim(),
      dob: form.dob,
      gender: opt(form.gender),
      phoneNumber: opt(form.phoneNumber),
    },
    referral: {
      source: form.source,
      discipline: form.discipline,
      receivedDate: form.receivedDate,
      referralDate: opt(form.referralDate),
      requestedSOCDate: opt(form.requestedSOCDate),
      referringProvider: opt(form.referringProvider),
      referringNPI: opt(form.referringNPI),
      referringPhone: opt(form.referringPhone),
      referringFax: opt(form.referringFax),
      notes: opt(form.notes),
    },
    ...(confirmNotDuplicate ? { confirmNotDuplicate: true } : {}),
  };
}

const inputClass = 'mt-1 w-full rounded-md border border-slate-300 p-2 text-sm bg-white';
const labelClass = 'block text-sm font-medium text-slate-700';

/**
 * "New referral" intake (REF-01 / REF-03 / EX-11): creates a pending_admission patient shell and
 * its referral in one call. A possible duplicate (name + DOB) is a warning, not a block: the user
 * can open the existing chart or create the new patient anyway.
 */
const IntakeDialog: React.FC<IntakeDialogProps> = ({ open, onCancel, onOpenPatient }) => {
  const servicesOffered = useServicesOffered();
  const baseId = useId();
  const { showToast } = useToast();
  const [form, setForm] = useState<IntakeForm>(emptyForm);
  const [submitted, setSubmitted] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [duplicates, setDuplicates] = useState<PossibleDuplicatePatient[] | null>(null);

  useEffect(() => {
    if (!open) return;
    setForm(emptyForm());
    setSubmitted(false);
    setBusy(false);
    setError(null);
    setDuplicates(null);
  }, [open]);

  const set = (field: keyof IntakeForm) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
    setForm((prev) => ({ ...prev, [field]: e.target.value }));

  const missing = REQUIRED.filter((key) => !form[key].trim());
  const fieldError = (key: RequiredKey) => submitted && missing.includes(key);

  const submit = async (confirmNotDuplicate: boolean) => {
    setSubmitted(true);
    if (missing.length > 0 || busy) return;
    setBusy(true);
    setError(null);
    try {
      const result = await ReferralsService.intakeReferral(buildIntakeRequest(form, confirmNotDuplicate));
      if (result.kind === 'duplicate') {
        setDuplicates(result.possibleDuplicates);
        return;
      }
      const missingInfo = result.referral?.missingInfo ?? [];
      if (missingInfo.length > 0) {
        const labels = missingInfo.map((key) => MISSING_INFO_LABELS[key] ?? key).join(', ');
        showToast(`Referral saved. Missing information: ${labels}.`, { variant: 'warning' });
      } else {
        showToast('Referral saved. The patient is pending admission.', { variant: 'success' });
      }
      onOpenPatient(mapPatientResponse(result.patient));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to save the referral.');
    } finally {
      setBusy(false);
    }
  };

  const openExisting = async (duplicate: PossibleDuplicatePatient) => {
    setBusy(true);
    setError(null);
    try {
      onOpenPatient(await fetchPatientById(duplicate.patientId));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to open the existing chart.');
    } finally {
      setBusy(false);
    }
  };

  const titleId = `${baseId}-title`;
  const id = (name: string) => `${baseId}-${name}`;

  return (
    <Dialog open={open} onClose={() => !busy && onCancel()} labelledBy={titleId}>
      {duplicates ? (
        <div className="text-left">
          <h3 id={titleId} className="text-lg font-semibold text-slate-800 pr-6">
            Possible duplicate patient
          </h3>
          <p className="mt-1 text-sm text-slate-600">
            A patient with the same name and date of birth already exists. Open the existing chart to add the referral
            there, or create a new patient anyway.
          </p>
          <ul className="mt-3 divide-y divide-slate-200 rounded-md border border-slate-200" data-testid="possible-duplicates">
            {duplicates.length === 0 && <li className="p-3 text-sm text-slate-600">No details were returned.</li>}
            {duplicates.map((dup) => (
              <li key={dup.patientId} className="flex flex-wrap items-center justify-between gap-2 p-3">
                <div className="flex items-center gap-2 text-sm">
                  <span className="font-medium text-slate-800">MRN {dup.mrn || '—'}</span>
                  <StatusPill status={dup.status} />
                </div>
                <button
                  type="button"
                  onClick={() => openExisting(dup)}
                  disabled={busy}
                  className="rounded border border-slate-300 bg-white px-3 py-1.5 text-sm text-slate-800 hover:bg-slate-50 disabled:opacity-50"
                >
                  Open existing chart
                </button>
              </li>
            ))}
          </ul>
          {error && (
            <p role="alert" className="mt-3 text-sm text-red-700">
              {error}
            </p>
          )}
          <div className="mt-5 flex flex-wrap justify-end gap-2">
            <button
              type="button"
              onClick={() => setDuplicates(null)}
              disabled={busy}
              className="px-4 py-2 rounded-md text-sm border border-slate-300 text-slate-700 bg-white hover:bg-slate-50 disabled:opacity-60"
            >
              Back
            </button>
            <button
              type="button"
              onClick={() => submit(true)}
              disabled={busy}
              className="px-4 py-2 rounded-md text-sm bg-slate-800 hover:bg-slate-700 text-white disabled:opacity-50"
            >
              {busy ? 'Saving…' : 'Create anyway'}
            </button>
          </div>
        </div>
      ) : (
        <form
          className="text-left"
          noValidate
          onSubmit={(e) => {
            e.preventDefault();
            void submit(false);
          }}
        >
          <h3 id={titleId} className="text-lg font-semibold text-slate-800 pr-6">
            New referral
          </h3>
          <p className="mt-1 text-sm text-slate-600">Creates the patient as Pending admission with this referral.</p>

          <fieldset className="mt-4">
            <legend className="text-sm font-semibold text-slate-800">Patient</legend>
            <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label htmlFor={id('first')} className={labelClass}>First name <span className="text-red-600" aria-hidden="true">*</span></label>
                <input id={id('first')} value={form.firstName} onChange={set('firstName')} className={inputClass} aria-invalid={fieldError('firstName')} autoComplete="off" />
              </div>
              <div>
                <label htmlFor={id('last')} className={labelClass}>Last name <span className="text-red-600" aria-hidden="true">*</span></label>
                <input id={id('last')} value={form.lastName} onChange={set('lastName')} className={inputClass} aria-invalid={fieldError('lastName')} autoComplete="off" />
              </div>
              <div>
                <label htmlFor={id('dob')} className={labelClass}>Date of birth <span className="text-red-600" aria-hidden="true">*</span></label>
                <input id={id('dob')} type="date" value={form.dob} max={toIsoDate(new Date())} onChange={set('dob')} className={inputClass} aria-invalid={fieldError('dob')} />
              </div>
              <div>
                <label htmlFor={id('gender')} className={labelClass}>Gender</label>
                <select id={id('gender')} value={form.gender} onChange={set('gender')} className={inputClass}>
                  <option value="">Not specified</option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              <div className="sm:col-span-2">
                <label htmlFor={id('phone')} className={labelClass}>Phone</label>
                <input id={id('phone')} type="tel" value={form.phoneNumber} onChange={set('phoneNumber')} className={inputClass} />
              </div>
            </div>
          </fieldset>

          <fieldset className="mt-4">
            <legend className="text-sm font-semibold text-slate-800">Referral</legend>
            <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label htmlFor={id('source')} className={labelClass}>Referral source <span className="text-red-600" aria-hidden="true">*</span></label>
                <select id={id('source')} value={form.source} onChange={set('source')} className={inputClass} aria-invalid={fieldError('source')}>
                  <option value="">Select source…</option>
                  {REFERRAL_SOURCE_OPTIONS.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor={id('discipline')} className={labelClass}>Requested service <span className="text-red-600" aria-hidden="true">*</span></label>
                <select id={id('discipline')} value={form.discipline} onChange={set('discipline')} className={inputClass} aria-invalid={fieldError('discipline')}>
                  <option value="">Select discipline…</option>
                  {servicesOffered.map((code) => ({ value: code, label: disciplineReferralLabel(code) })).map((d) => (
                    <option key={d.value} value={d.value}>{d.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor={id('received')} className={labelClass}>Received date <span className="text-red-600" aria-hidden="true">*</span></label>
                <input id={id('received')} type="date" value={form.receivedDate} onChange={set('receivedDate')} className={inputClass} aria-invalid={fieldError('receivedDate')} />
              </div>
              <div>
                <label htmlFor={id('referral-date')} className={labelClass}>Referral date</label>
                <input id={id('referral-date')} type="date" value={form.referralDate} onChange={set('referralDate')} className={inputClass} />
              </div>
              <div className="sm:col-span-2">
                <label htmlFor={id('soc')} className={labelClass}>Requested start of care</label>
                <input id={id('soc')} type="date" value={form.requestedSOCDate} onChange={set('requestedSOCDate')} className={inputClass} />
              </div>
            </div>
          </fieldset>

          <fieldset className="mt-4">
            <legend className="text-sm font-semibold text-slate-800">Referring physician (optional)</legend>
            <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="sm:col-span-2">
                <label htmlFor={id('provider')} className={labelClass}>Name</label>
                <input id={id('provider')} value={form.referringProvider} onChange={set('referringProvider')} className={inputClass} />
              </div>
              <div>
                <label htmlFor={id('npi')} className={labelClass}>NPI</label>
                <input id={id('npi')} value={form.referringNPI} onChange={set('referringNPI')} className={inputClass} inputMode="numeric" />
              </div>
              <div>
                <label htmlFor={id('ref-phone')} className={labelClass}>Phone</label>
                <input id={id('ref-phone')} type="tel" value={form.referringPhone} onChange={set('referringPhone')} className={inputClass} />
              </div>
              <div>
                <label htmlFor={id('fax')} className={labelClass}>Fax</label>
                <input id={id('fax')} type="tel" value={form.referringFax} onChange={set('referringFax')} className={inputClass} />
              </div>
            </div>
            {!form.referringProvider.trim() && (
              <p className="mt-1 text-xs text-slate-500">Without a physician the referral is flagged "Missing information".</p>
            )}
          </fieldset>

          <div className="mt-4">
            <label htmlFor={id('notes')} className={labelClass}>Notes</label>
            <textarea id={id('notes')} value={form.notes} onChange={set('notes')} rows={3} className={inputClass} />
          </div>

          {submitted && missing.length > 0 && (
            <p role="alert" className="mt-3 text-sm text-red-700">
              Complete the required fields: patient name, date of birth, referral source, requested service and received date.
            </p>
          )}
          {error && (
            <p role="alert" className="mt-3 text-sm text-red-700">
              {error}
            </p>
          )}

          <div className="mt-5 flex justify-end gap-2">
            <button
              type="button"
              onClick={onCancel}
              disabled={busy}
              className="px-4 py-2 rounded-md text-sm border border-slate-300 text-slate-700 bg-white hover:bg-slate-50 disabled:opacity-60"
            >
              Cancel
            </button>
            <button type="submit" disabled={busy} className="px-4 py-2 rounded-md text-sm bg-slate-800 hover:bg-slate-700 text-white disabled:opacity-50">
              {busy ? 'Saving…' : 'Save referral'}
            </button>
          </div>
        </form>
      )}
    </Dialog>
  );
};

export default IntakeDialog;
